import React, { useState, useEffect } from 'react';
import {
    Table,
    Message,
    Loader,
    Popup,
    Label,
    Container,
    Button
} from 'semantic-ui-react';
import ReactGA from 'react-ga4';

import { getAllInvestorProfiles } from '~/api/company';
import { useUserContext } from '~/hooks';
import CompanyBadge from '../company/CompanyBadge';
import BuyerNetworkFilters from './BuyerNetworkFilters';
import './styles.scss';

interface InvestorProfile {
    id: string;
    companyId: string;
    company?: {
        id: string;
        name: string;
    };
    maCapacity?: string;
    targetDealSize?: string;
    offTake?: string;
    projectStage?: string;
    states?: string;
    technology?: string;
    badgeIds?: number[];
    investorBadges?: any[];
    isVisible?: boolean;
    latestActivity?: string;
}

const BuyerNetwork: React.FC = () => {
    const [profiles, setProfiles] = useState<InvestorProfile[]>([]);
    const [filteredProfiles, setFilteredProfiles] = useState<InvestorProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { loggedInUser } = useUserContext();
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [resetFiltersTrigger, setResetFiltersTrigger] = useState<boolean>(false);
    const isAdmin = loggedInUser?.isAdmin;

    ReactGA.send({
        hitType: 'pageview',
        page: '/buyer-network',
        title: 'Buyer Network | Conductor Solar'
    });
    document.title = 'Buyer Network | Conductor Solar';

    useEffect(() => {
        loadProfiles();
    }, []);

    const loadProfiles = async () => {
        try {
            setLoading(true);
            const data = await getAllInvestorProfiles();
            // Filter to only show visible profiles (unless admin)
            const visibleProfiles = isAdmin ? data : data.filter((profile: InvestorProfile) => profile.isVisible);
            setProfiles(visibleProfiles);
            setFilteredProfiles(visibleProfiles);
        } catch (err: any) {
            setError(err.message || 'Failed to load investor profiles');
        } finally {
            setLoading(false);
        }
    };

    const formatStates = (states?: string) => {
        if (!states) return '-';
        if (states === 'ALL') return 'All states';

        const stateArray = states.split(',').filter(Boolean);
        return stateArray;
    };

    const renderOfftakeCell = (offTake?: string) => {
        if (!offTake) return '-';
        if (offTake === 'ALL') return 'All';

        const valueMap: Record<string, string> = {
            PPA: 'PPA',
            COMMUNITY: 'Community',
            UTILITY: 'Utility'
        };

        const values = offTake.split(',').map(v => v.trim()).filter(Boolean);
        const displayValues = values.map(v => valueMap[v] || v);

        return displayValues.join(', ');
    };

    const renderProjectStageBadges = (projectStage?: string) => {
        if (!projectStage) return '-';
        if (projectStage === 'ALL') return <Label color="grey">All</Label>;

        const values = projectStage.split(',').map(v => v.trim()).filter(Boolean);
        const displayMap: Record<string, string> = {
            EARLY_DEV: 'Early Dev',
            MID_DEV: 'Mid Dev',
            AT_OR_NEARING_NTP: 'NTP',
            MID_CONSTRUCTION: 'Mid-Construction',
            OPERATING: 'Operating'
        };

        const colorMap: any = {
            EARLY_DEV: '#488bb3',
            MID_DEV: '#03598c',
            AT_OR_NEARING_NTP: '#b5cc18',
            MID_CONSTRUCTION: '#fbbd08',
            OPERATING: '#f5ddcc'
        };

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {Object.entries(displayMap).map(([key, label], idx) => {
                    const isPresent = values.includes(key);
                    return (
                        <Label
                            key={idx}
                            style={{
                                margin: 0,
                                backgroundColor: isPresent ? colorMap[key] : '#e0e1e2',
                                color: isPresent
                                    ? (key === 'EARLY_DEV' || key === 'MID_DEV' ? '#fff' : '#000')
                                    : '#888',
                                opacity: isPresent ? 1 : 0.5,
                            }}
                        >
                            {label}
                        </Label>
                    );
                })}
            </div>
        );
    };

    const renderGeographyCell = (states?: string) => {
        const formattedStates = formatStates(states);

        if (formattedStates === '-' || formattedStates === 'All states') {
            return <span>{formattedStates}</span>;
        }

        const stateArray = formattedStates as string[];

        return (
            <Popup
                trigger={
                    <span>
                        {stateArray.length} state{stateArray.length !== 1 ? 's' : ''}
                    </span>
                }
                content={
                    <div>
                        {stateArray.map((state, index) => (
                            <Label key={index} size="small" style={{ margin: '2px' }}>
                                {state}
                            </Label>
                        ))}
                    </div>
                }
                on="hover"
                position="top center"
            />
        );
    };

    const renderTechnologyCell = (technology?: string) => {

        const valueMap: Record<string, string> = {
            PV: 'PV',
            BESS_PV: 'BESS with PV',
            BESS_STANDALONE: 'BESS standalone',
            MICROGRIDS: 'Microgrids',
            CHP: 'CHP',
            EV_CHARGING: 'EV Charging',
            OTHER: 'Other'
        };

        const values = (technology ? technology.split(',').map(v => v.trim()).filter(Boolean) : []);
        const displayValues = values.map(v => valueMap[v] || v);

        if (displayValues.length === 0) return <span>-</span>;

        return (
            <Popup
                trigger={
                    <span>
                        {displayValues.length} type{displayValues.length !== 1 ? 's' : ''}
                    </span>
                }
                content={
                    <div>
                        {displayValues.map((state, index) => (
                            <Label key={index} size="small" style={{ margin: '2px' }}>
                                {state}
                            </Label>
                        ))}
                    </div>
                }
                on="hover"
                position="top center"
            />
        );
    };

    const handleFilterChange = (filters: any) => {
        setResetFiltersTrigger(false);
        setShowFilters(false);
        const filtered = profiles.filter((profile) => {
            const matchesTargetDealSize = !filters.targetDealSize || profile.targetDealSize === filters.targetDealSize;
            const matchesMaCapacity = !filters.maCapacity || profile.maCapacity === filters.maCapacity;
            const matchesState = !filters.state || (profile.states && (profile.states.includes(filters.state) || profile.states === 'ALL'));
            const matchesOffTake = !filters.offTake || (profile.offTake && profile.offTake.includes(filters.offTake));
            const matchesProjectStage = !filters.projectStage || (profile.projectStage && profile.projectStage.includes(filters.projectStage));
            const matchesTechnology = !filters.technology || (profile.technology && profile.technology.includes(filters.technology));
            const matchesBadges =
                !filters.badgeIds ||
                filters.badgeIds.length === 0 ||
                (
                    profile.investorBadges &&
                    filters.badgeIds.every(
                        (id: number) =>
                            profile.investorBadges?.some((badge: any) => badge.id === id)
                    )
                );
            return matchesTargetDealSize && matchesMaCapacity && matchesState && matchesOffTake && matchesProjectStage && matchesTechnology && matchesBadges;
        });
        setFilteredProfiles(filtered);
    };

    const resetFilters = () => {
        setResetFiltersTrigger(true);
    };

    return (
        <>
            {loading ? (
                <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Loader active inline="centered" content="Loading buyer network..." />
                </div>
            ) : error ? (
                <Message negative>{error}</Message>
            ) : (
                <div id="buyer-network">
                    <Container>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', marginBottom: '1rem', marginTop: '1rem' }}>
                            <h1>Buyer Network</h1>
                            <p style={{ maxWidth: '800px', margin: '12px 0 0px' }}>
                                Explore our network of active buyers. This directory shows buyer profiles and their investment criteria to help you understand potential partners in the market.
                            </p>
                        </div>

                        <div className="table-meta-header">
                            <div>
                                <Button className="filters-toggle" onClick={() => setShowFilters(true)}>Filters</Button>
                                <span className="profile-count">
                                    Showing {filteredProfiles.length} of {profiles.length} {profiles.length === 1 ? 'profile' : 'profiles'} {filteredProfiles.length !== profiles.length ? <div>(<span onClick={resetFilters}>Reset filters</span>)</div> : null}
                                </span>
                            </div>
                        </div>

                        {profiles.length === 0 ? (
                            <Message info>
                                <Message.Header>No Investor Profiles</Message.Header>
                                <p>No investor profiles are currently available.</p>
                            </Message>
                        ) : (
                            <div className="table-container">
                                <Table celled striped style={{ overflowX: 'auto' }}>
                                    <Table.Header>
                                        <Table.Row>
                                            {isAdmin && <Table.HeaderCell>Company Name</Table.HeaderCell>}
                                            <Table.HeaderCell>Target Deal Size</Table.HeaderCell>
                                            <Table.HeaderCell>M&A Capacity</Table.HeaderCell>
                                            <Table.HeaderCell>Highlights</Table.HeaderCell>
                                            <Table.HeaderCell>Offtake</Table.HeaderCell>
                                            <Table.HeaderCell>Project Stage</Table.HeaderCell>
                                            <Table.HeaderCell>Geography</Table.HeaderCell>
                                            <Table.HeaderCell>Technology</Table.HeaderCell>
                                            <Table.HeaderCell>Last Active</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>

                                    <Table.Body>
                                        {filteredProfiles.map((profile) => (
                                            <Table.Row key={profile.id}>
                                                {isAdmin && (
                                                    <Table.Cell className="company-name">
                                                        {profile.company?.id ? (
                                                            <a href={`/company-profile/${profile.company.id}`}>
                                                                {profile.company.name}
                                                            </a>
                                                        ) : (
                                                            'Unknown Company'
                                                        )}
                                                    </Table.Cell>
                                                )}
                                                <Table.Cell className="target-deal-size">
                                                    {profile.targetDealSize || '-'}
                                                </Table.Cell>
                                                <Table.Cell className="ma-capacity">
                                                    {profile.maCapacity || '-'}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                        {profile.investorBadges && profile.investorBadges.length > 0 ? (
                                                            profile.investorBadges.map((investorBadge: any) => (
                                                                <CompanyBadge key={investorBadge.id} badge={investorBadge} />
                                                            ))
                                                        ) : (
                                                            <span>-</span>
                                                        )}
                                                    </div>
                                                </Table.Cell>

                                                <Table.Cell>
                                                    {renderOfftakeCell(profile.offTake)}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {renderProjectStageBadges(profile.projectStage)}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {renderGeographyCell(profile.states)}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {renderTechnologyCell(profile.technology)}
                                                </Table.Cell>

                                                <Table.Cell>
                                                    {profile.latestActivity ? new Date(profile.latestActivity).toLocaleDateString() : 'N/A'}
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            </div>
                        )}
                        <div id="filter-container" className={showFilters ? 'active' : ''}>
                            <BuyerNetworkFilters onFilterChange={handleFilterChange} triggerReset={resetFiltersTrigger} />
                        </div>
                    </Container>
                </div>
            )}

            {loggedInUser?.isInvestor && (
                <>
                    {window.location.assign('/dashboard')}
                </>
            )}
        </>
    );
};

export default BuyerNetwork;